import {
  Component,
  ChangeDetectionStrategy,
  HostListener,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  ViewEncapsulation,

} from "@angular/core";
import { IrbEditor } from "@irb/irb-editors/irb-editors.base";
import {
  IEvent,
  IrbEditorsEvents,
  IrbAppEvents,
  IrbClientsEvents,
  IrbSettingsEvents,
} from "@irb-common/events";
import { Observable, of } from "rxjs";
import {
  IAccountDetail,
  IAccountDetailSaveResponse,
  IDropDownDTO,
  IDropDownItemDTO,
  ISetAsideDto,
} from "../irb-editors.model";
import { HttpErrorResponse } from "@angular/common/http";
import { IAlertDialogData } from "@irb/app.model";
import { IDropdownItem } from "@irb-common/models/dropdown.model";
import { DropdownFilterComponent } from "@irb-common/components/dropdown-filter/dropdown-filter.component";
import { AnalyticsService } from "@irb-common/analytics/analytics.service";
import { IrbSettingsService } from "@irb/irb-settings/irb-settings.service";
import { DataTransferService } from "@irb-common/services/data-transfer.service";
import { IGlobalSettings } from "@irb/irb-settings/irb-settings.model";

@Component({ standalone: false,
  selector: "irb-editor-account",
  templateUrl: "./editor-account.component.html",
  styleUrls: [
    "./editor-account.component.scss",
    "../../irb-common/styles/dialog.scss",
  ],
  changeDetection: ChangeDetectionStrategy.Default,
  encapsulation: ViewEncapsulation.None,
})
export class EditorAccountComponent extends IrbEditor {
  @ViewChild("body") body: ElementRef;
  @ViewChild('irbSMASymbolDropdown') irbSMASymbolDropdown: DropdownFilterComponent;
  @ViewChild('irbFundingAccountDropdown') irbFundingAccountDropdown: DropdownFilterComponent;
  defaultExpandedPanels: Array<string>;

  specialTreatmentInit: boolean;
  accountdata: any;
  extAccountData: any;

  _isExternalAcct: boolean = false;
  _smaSymbol: IDropDownItemDTO;
  _fundingAccount: any;
  _accountDropDown: IDropDownDTO; //This will be used as funding account in case of SMA
  _securitiesDropDown: IDropDownDTO; //This will be used for SMA symbols in case of SMA
  setAsideData: ISetAsideDto[] = [];
  accountEventDataSource: string;
  globalSettings: IGlobalSettings;
  isUiTest = false;
  ENABLE_ACCOUNT_HISTORY = false;

  constructor(public dataTransferService: DataTransferService, private cdr: ChangeDetectorRef, public ats: AnalyticsService, private iss: IrbSettingsService) {
    super();
    this.identifier = "accountId";
    const irbAppConfig = this.as.getItem("IRB_APP_CONFIG");
    if (irbAppConfig && irbAppConfig.featureFlags) {
      this.ENABLE_ACCOUNT_HISTORY = irbAppConfig.featureFlags.ENABLE_ACCOUNT_HISTORY || false;
    }
  }

  get(event: IEvent) {
    this.accountEventDataSource = event.data?.source;
    super.get(event);
    this.ats.trackEditAccountEvent();

    this.getGlobalSettings()
      .then(() => this.getAccountDropDown(event.data.data.accountId))
      .then(() => this.getSecuritiesDropDown())
      .then(() => this.getSetAsideCash(event.data.data.accountId))
      .then(() => this.getAccoutExternalHoldings(event.data.data.accountId))
      .then(() => this.getAccountDetails(event.data.data.accountId))
  }

  getGlobalSettings() {
    return new Promise((resolve: any) => {
      this.iss.getGlobalSettings().subscribe((data) => {
        this.globalSettings = data as IGlobalSettings;
        resolve();
      });
    });
  }

  getAccountDropDown(accountId: string) {
    return new Promise((resolve: any) => {
      this.ies
        .getAccountDropDown(accountId)
        .subscribe((data: any) => {
          this.accountDropDown = data as IDropDownDTO;
          this.accountDropDown.items.forEach((x: any) => {
            x.value = x.name;
          });

          this.cdr.detectChanges();
          resolve();
        });
    });
  }

  getSecuritiesDropDown() {
    return new Promise((resolve: any) => {
      this.ies.getSecuritiesDropDown().subscribe((data: any) => {
        this.securitiesDropDown = data as IDropDownDTO;
        resolve();
      });
    });
  }

  getSetAsideCash(accountId: string) {
    return new Promise((resolve: any) => {
      this.ies
        .getSetAsideCash(accountId)
        .subscribe((data: any) => {
          this.setAsideData = data as ISetAsideDto[];
          resolve();
        });
    });
  }

  getAccoutExternalHoldings(accountId: string) {
    return new Promise((resolve: any) => {
      this.ies
        .getAccoutExternalHoldings(accountId)
        .subscribe((data: any) => {
          this.extAccountData = data;
          resolve();
        });
    });
  }

  getAccountDetails(accountId: string) {
    return new Promise((resolve: any) => {
      this.ies
        .getAccountDetails(accountId)
        .subscribe((data: any) => {
          this.load(data as IAccountDetail);
          resolve();
        });
    });
  }

  load(data: IAccountDetail) {

    this.data = data;
    if (this.data) {
      this.data.extAcctHoldingsList = this.extAccountData;
    }

    this.isExternalAcct =
      this.data &&
      this.data.accountsubType &&
      this.data.accountsubType === "EXTERNAL";

    if (this.data && this.data.smaSymbolID && this.data.smaSymbolID > 0 && this.data.smaSymbolName) {
      this.smaSymbol = {
        value: parseInt(this.data.smaSymbolID),
        name: this.data.smaSymbolName
      } as IDropDownItemDTO;
    } else {
      this.smaSymbol = null;
    }

    this.cdr.detectChanges();

    if (this.data && this.data.fundingAccountNumber) {
      this.fundingAccount = {
        name: this.data.fundingAccountNumber,
        value: this.data.fundingAccountNumber,
      } as IDropDownItemDTO;

    } else {
      this.fundingAccount = null;
    }
    this.cdr.detectChanges();

    this.defaultExpandedPanels = this.getAllDefaultPanelList(data);

    if (this.data) {
      if (this.data.trxCashMinSetting === 'No minimum') {
        this.data.trxCashMinSetting = 'Set custom';
        this.data.trxCashMinValue = 0;
        this.data.trxCashMinInd = 'P';
      }
      this.data.isPercentage = (this.data.cashMinimum) ? this.data.cashMinimum.endsWith("%") : true;

      if (this.data.isGlobal) {
        this.data.cashMinimum = this.data.globalCashMinimum;
        this.data.cashTarget = this.data.globalCashTarget;
        this.data.cashMaximum = this.data.globalCashMaximum;
        this.data.isPercentage = (this.data.globalCashMinimum) ? this.data.globalCashMinimum.endsWith("%") : true;
      }
      this.originalData = this.util.clone(this.data);
    }
    if (this.accountEventDataSource) {
      this.initExpansionPanels(this.accountEventDataSource);
      this.accountEventDataSource = null;
    } else {
      this.ies.loadExpansionState(
        this.defaultExpandedPanels.length,
        "editor-account",
        this.defaultExpandedPanels,
        false
      );

    }


    this.cdr.detectChanges();
    super.load(data);
    if (!this.isUiTest)
    setTimeout(() => this.onResize());
  }

  getAllDefaultPanelList(data: IAccountDetail) {
    const expandedPanels = ["PORTFOLIO_ASSIGNMENT", "SPECIAL_TREATMENT", "SMA", "CASH_NEEDS", "EXTERNAL_ACCOUNTS"];
    let panels: string[] = [];
    if (data) {

      expandedPanels.forEach((componentId: string) => {
        switch (componentId) {
          case 'PORTFOLIO_ASSIGNMENT':
            panels.push(componentId);
            break;
          case 'SPECIAL_TREATMENT':
            panels.push(componentId);
            break;
          case 'SMA':
            if (this.data.isSMAapplicable) {
              panels.push(componentId);
            }
            break;
          case 'CASH_NEEDS':
            panels.push(componentId);
            break;
          case 'EXTERNAL_ACCOUNTS':
            if (
              this.data &&
              this.data.accountsubType &&
              this.data.accountsubType === "EXTERNAL") {
              panels.push(componentId);
            }
            break;
        }
      });
    } else {
      panels = ["PORTFOLIO_ASSIGNMENT", "SPECIAL_TREATMENT", "CASH_NEEDS"];
    }

    return panels;
  }
  initExpansionPanels(eventDataSource: string) {
    let cashNeedsTabInit = false, specialTreatmentTabInit = false;

    if (eventDataSource === "cshNeeds") {
      cashNeedsTabInit = true;
    } else if (eventDataSource === 'specialTreatment') {
      specialTreatmentTabInit = true;
    }
    let dep = this.defaultExpandedPanels;
    let sessionOverridesDefault = true;
    if (cashNeedsTabInit) {
      dep = ["CASH_NEEDS"];
      sessionOverridesDefault = false;
    } else if (specialTreatmentTabInit) {
      dep = ["SPECIAL_TREATMENT"];
      sessionOverridesDefault = false;
    }
    this.ies.loadExpansionState(
      this.defaultExpandedPanels.length,
      "editor-account",
      dep, sessionOverridesDefault
    );
  }
  getCashNeedsForSave(value: any) {
    if (value && value.includes("$-")) {
      value = value.replace("$-", "-$");
      return value;
    }
    return value;
  }
  save() {
    this.ats.trackClickEvent("lc_edit_save");
    let dataToSave= this.data;

    this.validateInput().subscribe((errors) => {
      if (errors.length > 0) {
        let errMsg: string = errors.join("\n");

        this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
          dTitle: "Alert",
          msg: errMsg,
          dProceedBtnLabel: "Ok",
          dType: "ERROR"
        } as IAlertDialogData);
      } else {
        if(this.data.isSMA) {
          this.data.oSetAsideDTO?.forEach((element:any) => {
            element.isDeleted=true;
          });
        } else{
          dataToSave.smaSymbolID=null;
          dataToSave.smaSymbolName=null;
        }

        this.as.fireEvent(IrbEditorsEvents.SAVE_START);
        // set Data cashMinimum and CashMaximum Values for $- to -$ for saving.
        this.data.cashMinimum = this.getCashNeedsForSave(this.data.cashMinimum);
        this.data.cashTarget = this.getCashNeedsForSave(this.data.cashTarget);
        this.data.cashMaximum = this.getCashNeedsForSave(this.data.cashMaximum);
        this.ies.saveAccountDetails(dataToSave).subscribe(
          (data: any) => {
            const response = data as IAccountDetailSaveResponse;

            if (response.responseStatus === "SUCCESS") {
              if (response.responseUtilityMap != null) {
                //TODO: Show validation warning
                // console.log(
                //   "response.responseUtilityMap : " + response.responseUtilityMap
                // );
              } else {
                const goClose = this.hasPendingFunction;
                this.as.fireEvent(IrbEditorsEvents.SAVE_SUCCESS, {
                  hasPendingFunction: this.hasPendingFunction,
                  data: "Account updated.",
                });
                // this.as.fireEvent(IrbEditorsEvents.ACCOUNT_SAVED);
                // this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT);

                this.as.fireEvent(IrbAppEvents.GRID_REFRESH);
                if (!goClose) {
                  this.as.fireEvent(IrbEditorsEvents.CLOSE_WITHOUT_SAVE, { "slot": this.slot });
                }

              }
            } else if (response.responseStatus === "STALEDATAEXCEPTION") {
              this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT);
              this.as.fireEvent(IrbAppEvents.SHOW_CONFIRMATION, {
                dTitle: 'Refresh Required',
                dProceedBtnLabel: 'Refresh',
                msg: "You are attempting to edit an account that was just modified by someone else. To see the updated account, you need to refresh the page.",
                onProceed: () => {
                  this.getSetAsideCash(this.data.accountId);
                  this.cdr.detectChanges();
                  this.as.fireEvent(IrbAppEvents.GRID_REFRESH);
                },
                onCancel: () => {
                  this.revert();
                }

              } as IAlertDialogData);

            }

            else {
              this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR);
            }
          },
          () => {
            this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR);
          }
        );
      }
    });
  }

  @HostListener("window:resize", ["$event"])
  onResize() {
    if (this.body) {
      const rect = this.body.nativeElement.getBoundingClientRect();
      let h = window.innerHeight - rect.top - 10;

      h = h < 200 ? 200 : h;
      this.bodyHeight = h + "px";
    }
  }

  processEvent(event: IEvent) {
    super.processEvent(event);

    switch(event.type) {
      case IrbSettingsEvents.GLOBAL_SETTINGS_REFRESH:
        this.getGlobalSettings();
        break;
    }
  }

  onSMASymbolChange(event: any) {
    if (this.data.isSMA) {
      this.data.smaSymbolID = event.value;
      this.data.smaSymbolName = event.name;
      this.onInputChange(["isPercentage"]);
    }
  }

  getSMASymbolItems(searchStr: any) {
    let items = (this.securitiesDropDown?.items && this.securitiesDropDown?.items.length > 0) ? this.securitiesDropDown?.items : [];
    const f: Array<IDropdownItem> = DropdownFilterComponent.getFilteredList(searchStr, items);
    this.irbSMASymbolDropdown.set(f);
  }

  getFundingAccountItems(searchStr: any) {
    let items = (this.accountDropDown?.items && this.accountDropDown?.items.length > 0) ? this.accountDropDown?.items : [];
    const f: Array<IDropdownItem> = DropdownFilterComponent.getFilteredList(searchStr, items);
    this.irbFundingAccountDropdown.set(f);
  }

  onSmaChange() {
    if (!this.data.isSMA) {
      this.data.smaSymbolID = this.originalData.smaSymbolID;
      this.data.smaSymbolName = this.originalData.smaSymbolName;
      this.data.fundingAccountNumber = this.originalData.fundingAccountNumber;
      this.smaSymbol = null;
      this.fundingAccount = null;
      this.onInputChange();
    } else {
      this.onInputChange();
    }
  }

  onFundingAccountChange(event: any) {
    if (this.data.isSMA) {
      this.data.fundingAccountNumber = event.name;
      this.onInputChange(["isPercentage"]);
    }
  }

  validateInput(): Observable<string[]> {
    this.cdr.detectChanges();
    const errors: string[] = [];

    if (this.data.isSMA) {
      if (
        this.data.smaSymbolName == null ||
        this.data.fundingAccountNumber == null
      ) {
        errors.push(
          "You must select an SMA symbol and funding account to proceed."
        );
      }
    }

    return of(errors);
  }

  showEditPortfolio(portfolioId: number) {
    this.checkForModifications(() => {
      this.as.fireEvent(IrbClientsEvents.SHOW_EDIT_PORTFOLIO, {
        data: {
          portfolioId: portfolioId,
          accountId: this.data.accountId,
        },
      });
    });
  }

  getCashNeedsIgnoreList() {
    return ((this.globalSettings?.useModelCash && this.data.isGlobal) || this.data.useModelCashAtAccount) ? ["cashMinimum","cashMaximum","cashTarget"] : [];
  }

  get isExternalAcct() {
    return this._isExternalAcct;
  }

  set isExternalAcct(isExtAct: boolean) {
    this._isExternalAcct = isExtAct;
  }

  get smaSymbol() {
    return this._smaSymbol;
  }

  set smaSymbol(smaSymbol: IDropDownItemDTO) {
    this._smaSymbol = smaSymbol;
  }

  get fundingAccount() {
    return this._fundingAccount;
  }

  set fundingAccount(fundingAccount: IDropDownItemDTO) {
    this._fundingAccount = fundingAccount;
  }

  get accountDropDown() {
    return this._accountDropDown;
  }

  set accountDropDown(accountDropDown: IDropDownDTO) {
    this._accountDropDown = accountDropDown;
  }

  get securitiesDropDown() {
    return this._securitiesDropDown;
  }

  set securitiesDropDown(securitiesDropDown: IDropDownDTO) {
    this._securitiesDropDown = securitiesDropDown;
  }
}
